import React from 'react';
import PropTypes from 'prop-types';
import {
  ThemeProvider as MuiThemeProvider,
  createMuiTheme as createLegacyModeTheme,
  unstable_createMuiStrictModeTheme as createStrictModeTheme,
} from '@material-ui/core/styles';




const themeInitialOptions = {
  direction: 'rtl',
  paletteColors: {

  },
  spacing: 8, // spacing unit
};

export const DispatchContext = React.createContext(() => {
  throw new Error('Forgot to wrap component in `ThemeProvider`');
});

if (process.env.NODE_ENV !== 'production') {
  DispatchContext.displayName = 'ThemeDispatchContext';
}

const useEnhancedEffect = typeof window === 'undefined' ? React.useEffect : React.useLayoutEffect;

let createMuiTheme;
if (process.env.REACT_MODE === 'legacy') {
  createMuiTheme = createLegacyModeTheme;
} else {
  createMuiTheme = createStrictModeTheme;
}

export function ThemeProvider(props) {
  const { children } = props;

  const [themeOptions, dispatch] = React.useReducer((state, action) => {
    switch (action.type) {
      case 'SET_SPACING':
        return {
          ...state,
          spacing: action.payload,
        };
      case 'INCREASE_SPACING': {
        return {
          ...state,
          spacing: state.spacing + 1,
        };
      }
      case 'DECREASE_SPACING': {
        return {
          ...state,
          spacing: state.spacing - 1,
        };
      }

      case 'RESET_COLORS':
        return {
          ...state,
          paletteColors: themeInitialOptions.paletteColors,
        };
      case 'CHANGE':
        return {
          ...state,
          paletteType: action.payload.paletteType || state.paletteType,
          direction: action.payload.direction || state.direction,
          paletteColors: action.payload.paletteColors || state.paletteColors,
        };
      default:
        throw new Error(`Unrecognized type ${action.type}`);
    }
  }, themeInitialOptions);

  /**
   * A4: light, always -- not `prefers-color-scheme`.
   *
   * The dominant thing on this page is the card gateway's iframe, and all three gateways
   * serve a light form we cannot restyle. Honouring a dark preference gave a payer with dark
   * mode on a dark page wrapped around a white form, and in places dark text on the dark
   * background where MUI's own palette had not been told about the brand colours.
   *
   * A5: the effect that used to sit here was dead -- `process.browser` is undefined under
   * CRA, so it never ran -- and it was trying to dispatch `secondary: { main: "#ff" }`, which
   * is not a colour, and `paletteType: false`, which is not a palette type. It would have
   * thrown had it ever executed.
   */
  const { direction, paletteColors, paletteType = 'light', spacing } = themeOptions;

  useEnhancedEffect(() => {
    document.body.dir = direction;
  }, [direction]);

  const theme = React.useMemo(() => {
    const nextTheme = createMuiTheme(
      {
        direction,
        palette: {
          type: paletteType,
          ...paletteColors,
        },
        spacing,
      },
      //languageMap[userLanguage],
    );


    return nextTheme;
  }, [direction, paletteColors, paletteType, spacing]);



  return (
    <MuiThemeProvider theme={theme}>
      <DispatchContext.Provider value={dispatch}>{children}</DispatchContext.Provider>
    </MuiThemeProvider>
  );
}

ThemeProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * @returns {(nextOptions: Partial<typeof themeInitialOptions>) => void}
 */
export function useChangeTheme() {
  const dispatch = React.useContext(DispatchContext);
  return React.useCallback((options) => dispatch({ type: 'CHANGE', payload: options }), [dispatch]);
}
